import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Input,
  message,
  PageHeader,
  Select,
  Form,
  Row,
  Col,
  DatePicker,
  Spin,
  Typography,
  Popconfirm,
  Table,
  Tag,
  Divider,
  Tabs,
  Tooltip,
  Statistic,
  Modal,
} from "antd";
import moment from "moment";
import Page from "_components/Page";
import {
  companyService,
  currencyService,
  materialDetailService,
  stockIssueService,
  taxTypeService,
} from "_services";
import {
  FileExcelOutlined,
  FilePdfOutlined,
  CloseOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Auth } from "_helpers";
import Checkbox from "antd/lib/checkbox/Checkbox";
import TabMenu from "./TabMenu";
import Stepper from "./Stepper";
import { CurrencyFormatter } from "_helpers/formatters";
import { render } from "react-dom/cjs/react-dom.production.min";
import { set } from "lodash";

function TiepSummary({ history, match }) {
  const { id } = match.params;
  const { path } = match;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingStockItem, setLoadingStockItem] = useState(false);
  const [loadingSummaryPdf, setLoadingSummaryPdf] = useState(false);
  const [loadingDetailedPdf, setLoadingDetailedPdf] = useState(false);
  const [generatingMudPdf, setGeneratingMudPdf] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingDetailsUpdate, setLoadingDetailsUpdate] = useState(false);
  const [stockIssue, setStockIssue] = useState({});
  const [stockIssueLines, setStockIssueLines] = useState([]);
  const [selectedIssueItemKeys, setSelectedIssueItemKeys] = useState([]);
  const [stores, setStores] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [issuedStocks, setIssuedStocks] = useState([]);
  const [receivedStocks, setReceivedStocks] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState(0);
  const [activeSubItemTabKey, setActiveSubItemTabKey] = useState(0);
  const [selectedRowKeyMap, setSelectedRowKeyMap] = useState(new Map());
  const [selectedIssueLineKeys, setSelectedIssueLineKeys] = useState([]);
  const [selectedLineMaterials, setSelectedLineMaterials] = useState(null);
  const [selectedMaterialBreakdown, setSelectedMaterialBreakdown] =
    useState(null);
  const [selectedGrnLineKeys, setSelectedGrnLineKeys] = useState([]);
  const [selectedLineRowKeys, setSelectedLineRowKeys] = useState(null);
  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedItemRowKeys, setSelectedItemRowKeys] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [taxTypes, setTaxTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [availableStocks, setAvailableStocks] = useState([]);
  const { Option } = Select;
  const { Text } = Typography;
  const { TextArea } = Input;

  useEffect(() => {
    const fetchData = async () => {
      await getStockIssue(id);
      await getTaxTypes();
    };
    fetchData();
  }, []);

  const getTaxTypes = async () => {
    try {
      const data = await taxTypeService.getAll();
      setTaxTypes(data.content);
    } catch (error) {
      setError(error);
      message.error(error);
    }
  };

  const getStockIssue = async (id) => {
    try {
      setLoading(true);
      const response = await stockIssueService.getByViewId(id);
      const stockIssue = response.content;
      setStockIssue(stockIssue);
      onFill(stockIssue);
      if (stockIssue?.stockIssueLines?.length == 1) {
        setSelectedLineRowKeys([stockIssue?.stockIssueLines[0].id.toString()]);
        setSelectedLine(stockIssue?.stockIssueLines[0]);
      }
    } catch (error) {
      setError(error);
      message.error(error ?? "Error getting stock Issues!");
    } finally {
      setLoading(false);
    }
  };

  const onSearchAndAddStockItem = (value) => {
    if (value) {
      setLoadingStockItem(true);
      materialDetailService
        .search(`?barcode=${encodeURIComponent(value)}`)
        .then((data) => {
          if (data.content && data.content[0]) {
            let stock = data.content[0];
            let matchedIssueLine = null;

            const deliveryItem = stockIssueLines
              .flatMap((line) => line.issuedItems)
              .find((item) => item.id === stock.id);
            if (deliveryItem) {
              if (deliveryItem.added) {
                message.info("Already loaded this stock item");
              } else {
                for (const line of stockIssueLines) {
                  if (
                    line.issuedItems &&
                    line.issuedItems.find((item) => item.id === stock.id)
                  ) {
                    handleTabChange(line.key);
                    matchedIssueLine = line;
                  }
                }

                const lineKey = `${matchedIssueLine.stockType.id}`;
                if (selectedRowKeyMap.has(lineKey)) {
                  selectedRowKeyMap.set(lineKey, [
                    ...selectedRowKeyMap.get(lineKey),
                    stock.id,
                  ]);
                } else {
                  selectedRowKeyMap.set(lineKey, [stock.id]);
                }

                updateStockItemStatus();
                message.success("Successfully loaded to the delivery");
              }
            } else {
              message.error("Sorry, No item found against the added stocks");
            }

            form.setFieldsValue({ stockItem: null });
          } else {
            message.error("Sorry, No item found against the barcode.");
          }
          setLoadingStockItem(false);
        })
        .catch((error) => {
          message.error(`${error}`);
          setLoadingStockItem(false);
        });
    } else {
      message.error("Please enter valid barcode.");
    }
  };

  const handleFormSumbit = () => {
    form
      .validateFields()
      .then((values) => {
        setLoadingUpdate(true);
        delete values.issueNumber;
        delete values.issuedDate;
        delete values.store;
        delete values.vehicleNumber;
        delete values.stockItem;

        values.issuedLines = stockIssueLines.map((line) => ({
          issuedItems: line.issuedItems
            .filter((item) => item.added)
            .map((item) => ({
              id: item.id,
              mode: "DELIVERED",
            })),
        }));
        values.deliveredDate = moment(values.deliveredDate).format(
          "YYYY-MM-DD HH:mm:ss"
        );
        console.log("Update: ", values);
        stockIssueService
          .deliveryUpdate(id, values)
          .then(() => {
            setLoadingUpdate(false);
            message.success("Delivery success!");
            history.push("..");
          })
          .catch((error) => {
            setLoadingUpdate(false);
            message.error(`${error}`);
          });
      })
      .catch((errorInfo) => {});
  };

  const onSelectChange = (rowKeys) => {
    setSelectedRowKeyMap(selectedRowKeyMap.set(activeTabKey, rowKeys));
    updateStockItemStatus();
  };

  const updateStockItemStatus = () => {
    let allSelectedKeys = [];
    for (let value of selectedRowKeyMap.values()) {
      allSelectedKeys.push(...value);
    }

    setSelectedRowKeys(allSelectedKeys);
    const totalIssuedStocks = stockIssueLines.flatMap(
      (item) => item.issuedItems
    );
    let unLoadedStocks = [];
    for (let item of totalIssuedStocks) {
      if (allSelectedKeys.includes(item.id)) {
        item.added = true;
        item.mode = "UNLOADED";
        unLoadedStocks.push(item);
      } else {
        item.added = false;
        item.mode = "ISSUED";
      }
    }
    setReceivedStocks(unLoadedStocks);

    stockIssueLines.forEach((line) => {
      line.receivedQuantity = 0;
      line.receivedQty = 0;
      line.addedAll = false;
      unLoadedStocks.forEach((stock) => {
        if (line.requestLine.stockType.id === stock.stockType.id) {
          line.receivedQuantity += 1;
          line.receivedQty += stock.quantity;
        }
      });

      if (line.quantity === line.receivedQuantity) {
        line.addedAll = true;
      }
    });
  };

  const handlePrintPdf = (summaryFlag) => {
    if (summaryFlag) {
      setLoadingSummaryPdf(true);
    } else {
      setLoadingDetailedPdf(true);
    }

    stockIssueService
      .printPdf(id, summaryFlag)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Stock-Delivery-${summaryFlag ? "Summary_" : "Detailed_"}${
            stockIssue.issueNumber
          }.pdf`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        if (summaryFlag) {
          setLoadingSummaryPdf(false);
        } else {
          setLoadingDetailedPdf(false);
        }
      })
      .catch((error) => {
        if (summaryFlag) {
          setLoadingSummaryPdf(false);
        } else {
          setLoadingDetailedPdf(false);
        }
        message.error(error);
      });
  };

  const handlePrintMudPdf = async () => {
    try {
      setGeneratingMudPdf(true);
      const response = await stockIssueService.printMudPdf(id);
      message.success(
        "Generated Mud document successfully, check documents tab"
      );
    } catch (error) {
      if (error.response?.status === 400) {
        message.error(
          "Mud document Already exists!, Please Check Documents Tab"
        );
      } else {
        setError(error);
      }
    } finally {
      setGeneratingMudPdf(false);
    }
  };

  const handleExportExcel = () => {
    setLoadingExcel(true);
    stockIssueService
      .printExcel(id)
      .then((response) => {
        const url = window.webkitURL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Stock-Issue_${stockIssue.issueNumber}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        setLoadingExcel(false);
      })
      .catch((error) => {
        setLoadingExcel(false);
        message.error(error);
      });
  };

  const handleUpdateDetails = () => {
    form
      .validateFields()
      .then((values) => {
        setLoadingDetailsUpdate(true);
        const payLoad = {
          vehicle: values.vehicle,
        };
        stockIssueService
          .updateDetails(id, payLoad)
          .then(() => {
            message.success("Successfully updated!");
            setLoadingDetailsUpdate(false);
            history.push(`${path}`.replace(":id", id));
          })
          .catch((error) => {
            setLoadingDetailsUpdate(false);
            message.error(`${error}`);
          });
      })
      .catch((error) => {
        message.error(`${error}`);
      });
  };

  const onFill = (data) => {
    const totalAmount =
      (data?.totalDutyAmount ?? 0) + (data?.totalVatAmount ?? 0);
    form.setFieldsValue({
      ...(data || {}),
      totalAmount: Number(
        data?.totalLiabilityAmount + data?.totalVatAmount
      ).toFixed(2),
      stockIssueType:
        data?.stockIssueType === "DERECT_Export" ? "DIRECT" : "INDIRECT",
    });
  };

  const handleAllUnloadChange = (e) => {
    if (e.target.checked) {
      stockIssueLines.forEach((line, index) => {
        setSelectedRowKeyMap(
          selectedRowKeyMap.set(
            line.key,
            line.issuedItems.map((item) => item.id)
          )
        );
      });

      updateStockItemStatus();
    } else {
      setSelectedRowKeyMap(new Map());
      const totalIssuedStocks = stockIssueLines.flatMap(
        (item) => item.issuedItems
      );
      for (let item of totalIssuedStocks) {
        item.added = false;
        item.mode = "ISSUED";
      }
      setReceivedStocks([]);
      stockIssueLines.forEach((line) => {
        line.receivedQuantity = 0;
        line.receivedNetWeight = 0;
        line.receivedGrossWeight = 0;
        line.receivedQty = 0;
        line.addedAll = false;
      });
    }
  };

  const getAvailableItems = (stockTypeId) => {
    setLoading(true);
    stockIssueService
      .getAvailableStockReceipts("?stockTypeId=" + stockTypeId)
      .then((data) => {
        setAvailableStocks(data.content[0]);
        onFillModal(data.content[0].availableStockByReceiptViews[0] ?? []);
        setLoading(false);
      })
      .catch((error) => {
        setError(error);
        setLoading(false);
        message.error(error);
      });
  };

  const onFillModal = (data) => {
    form.setFieldsValue({
      selectedStockReceipt: {
        id: data?.stockReciptId,
        customRefNumber: data?.customRefNumber,
        stockTypeName: data?.stockTypeName,
        stockTypeId: data?.stockTypeId,
        stockTypeCode: data?.stockTypeCode,
        packingType: data?.packingType,
        packingTypeId: data?.packingTypeId,
        totalQuantity: data?.totalQuantity,
        tiepQuantityPerMaterial: data?.tiepQuantityPerMaterial,
        receivedDate: data?.receivedDate,
        quantity: 0,
        uomName: data?.uomName,
        uomId: data?.uomId,
      },
    });
  };

  const handleAddItemClick = (item) => {
    setOpen(true);
    getAvailableItems(item.stockType.id);
  };

  const getTabContent = (stockIssueLines) => {
    let tabPanes = [];
    stockIssueLines?.forEach((line) => {
      if (line.issuedItems && line.issuedItems.length > 0) {
        tabPanes.push(
          <Tabs.TabPane
            tab={`${line.stockType?.code ?? ""} (${
              line.totalIssuedItems ?? 1
            })`}
            key={line.key}
          >
            <Table
              bordered
              size="small"
              rowKey="id"
              columns={getStockItemColumns()}
              dataSource={line.issuedItems}
              pagination={false}
              rowClassName={(record) => (record.added ? "ant-tag-green" : "")}
              rowSelection={{
                type: "radio",
                ...issueItemsRowSelection,
              }}
              summary={(pageData) => {
                let totalItems = 0;
                let totalQty = 0;
                let totalLiability = 0;

                pageData.forEach(({ quantity, liabilityAmount }) => {
                  totalItems += 1;
                  totalQty += quantity ? quantity : 1;
                  totalLiability += liabilityAmount ? liabilityAmount : 0;
                });

                form.setFieldValue(
                  "declarationValue",
                  CurrencyFormatter(totalLiability ?? "0")
                );

                return (
                  <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={3}>
                        <Text strong>Total</Text>
                      </Table.Summary.Cell>

                      {/* <Table.Summary.Cell colSpan={1} align="right">
                        <Text strong style={{ paddingLeft: 12 }}>
                          {CurrencyFormatter(totalLiability ?? "0")}
                        </Text>
                      </Table.Summary.Cell> */}
                      <Table.Summary.Cell
                        colSpan={1}
                        align="right"
                      ></Table.Summary.Cell>

                      <Table.Summary.Cell
                        colSpan={1}
                        align="right"
                      ></Table.Summary.Cell>

                      <Table.Summary.Cell
                        colSpan={2}
                        align="right"
                      ></Table.Summary.Cell>

                      <Table.Summary.Cell colSpan={1} align="right">
                        <Text strong style={{ paddingLeft: 12 }}>
                          {totalItems}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>
                );
              }}
            />
          </Tabs.TabPane>
        );
      }
    });

    return tabPanes;
  };

  const handleTabChange = (activeKey) => {
    setActiveTabKey(activeKey.toString());
    setSelectedItemRowKeys([activeKey.toString()]);
  };

  const getTabContentOfSubItems = (stockItems) => {
    let tabPanes = [];
    stockItems?.forEach((item) => {
      tabPanes.push(
        <Tabs.TabPane
          tab={`${item.stockType.code} (${item.quantity ?? 1})`}
          key={item.stockType.id}
        >
          {stockIssue?.orderStage === "READY" && (
            <Button
              style={{
                width: "100%",
                color: "black",
                fontWeight: "bold",
                marginTop: 16,
                marginBottom: 8,
                background: "rgb(190, 200, 200)",
              }}
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => handleAddItemClick(item)}
            >
              Add Item
            </Button>
          )}
          <Table
            bordered
            size="small"
            rowKey="id"
            columns={getSubItemColumns()}
            dataSource={item.stockItems}
            pagination={false}
            rowClassName={(record) => (record.added ? "ant-tag-green" : "")}
            summary={(pageData) => {
              let totalIssuedPackages = 0;
              let totalQty = 0;

              pageData.forEach(
                ({ tiepQuantityPerMaterial, issuedPackages }) => {
                  totalIssuedPackages += Number(issuedPackages);
                  totalQty += tiepQuantityPerMaterial;
                }
              );

              return (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={6}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell colSpan={1} align="right">
                      <Text strong style={{ paddingLeft: 12 }}>
                        {totalIssuedPackages}
                      </Text>
                    </Table.Summary.Cell>

                    <Table.Summary.Cell colSpan={1} align="right">
                      <Text strong style={{ paddingLeft: 12 }}>
                        {totalQty}
                      </Text>
                    </Table.Summary.Cell>

                    {stockIssue?.orderStage == "READY" && (
                      <Table.Summary.Cell
                        colSpan={1}
                        align="right"
                      ></Table.Summary.Cell>
                    )}
                  </Table.Summary.Row>
                </>
              );
            }}
          />
        </Tabs.TabPane>
      );
    });

    return tabPanes;
  };

  const onCancel = () => {
    history.push("/stock-issue");
  };

  const handleStatusUpdate = async () => {
    const currentOrderStage = stockIssue?.orderStage ?? "COMPLETE";
    if (currentOrderStage == "COMPLETE") return;

    const forwardState =
      currentOrderStage == "OPEN"
        ? "READY"
        : currentOrderStage == "READY"
        ? "ISSUED"
        : "COMPLETE";

    const params = { requestStatus: forwardState };

    try {
      setLoading(true);
      await stockIssueService.updateStatus(id, params);
      setStockIssue((prevIssue) => ({
        ...prevIssue,
        orderStage: forwardState,
      }));
      history.push(`${path}`.replace(":id", id));
    } catch (error) {
      setError(error);
      message.error(`Error:${error}`);
    } finally {
      setLoading(false);
    }
  };

  const getActionButtons = () => {
    let buttons = [];

    // if (Auth.hasRole(Auth.getProfile(), ["SAU", "CAU"])) {
    //   if (["ISSUED", "COMPLETE"].includes(stockIssue?.orderStage)) {

    if (stockIssue.orderStage !== "COMPLETE") {
      const Status =
        stockIssue.orderStage == "OPEN"
          ? "UPDATE"
          : stockIssue.orderStage == "READY"
          ? "ISSUE"
          : "COMPLETE";

      buttons.push(
        <Popconfirm
          title={`Are you sure to ${Status} it?`}
          onConfirm={(e) => {
            e.preventDefault();
            handleStatusUpdate();
          }}
          key="con-complete"
          //disabled={addedStocks.length === 0}
        >
          <Button key="btn-complete" htmlType="button" loading={loading}>
            {Status}
          </Button>
        </Popconfirm>
      );
    }

    buttons.push(
      <Tooltip key="tp-mud-pdf" placement="topLeft" title="MUD PDF">
        <Button
          key="mud-pdf"
          icon={<FilePdfOutlined />}
          onClick={(e) => {
            e.preventDefault();
            handlePrintMudPdf();
          }}
          loading={generatingMudPdf}
        >
          MUD Form
        </Button>
      </Tooltip>
    );

    buttons.push(
      <Button key="2" htmlType="button" onClick={onCancel}>
        Cancel
      </Button>
    );

    return buttons;
  };

  const onLineRowSelectChange = (selectedRowKeys, selectedRows) => {
    setSelectedLineRowKeys([selectedRowKeys[0]]);
    setSelectedLine(selectedRows[0]);
    setActiveTabKey(selectedRowKeys[0].toString());
  };

  const lineRowSelection = {
    type: "radio",
    selectedRowKeys: selectedLineRowKeys,
    onChange: onLineRowSelectChange,
  };

  const onItemRowSelectChange = (selectedItemRowKeys, selectedRows) => {
    setSelectedItemRowKeys([selectedItemRowKeys[0]]);
    setSelectedItem(selectedRows[0]);
    setActiveTabKey(selectedItemRowKeys[0].toString());
  };

  const issueItemsRowSelection = {
    type: "radio",
    selectedRowKeys: selectedItemRowKeys,
    onChange: onItemRowSelectChange,
  };

  const handleItemDelete = (record) => {
    setLoading(true);
    stockIssueService
      .deleteItem(id, record.id)
      .then((response) => {
        message.success("Item deleted successfully!");
        setLoading(true);
        getStockIssue(id);
      })
      .catch((error) => {
        setLoading(true);
        setError(error);
        message.error(error);
        setLoading(false);
      });
  };

  const getLineColumns = () => {
    const lineColumns = [];

    lineColumns.push({
      title: "HSCode",
      key: "stockTypeCode",
      dataIndex: ["stockTypeCode"],
    });

    lineColumns.push({
      title: "Description",
      key: "description",
      dataIndex: ["stockTypeName"],
      align: "left",
      render: (text, record) => {
        return text ?? "";
      },
    });

    lineColumns.push({
      title: "Quantity",
      key: "quantity",
      dataIndex: "quantity",
      align: "left",
      render: (text, record) => {
        return text ?? "";
      },
    });

    lineColumns.push({
      title: "Liability",
      dataIndex: ["totalLiabilityAmount"],
      key: "totalLiabilityAmount",
      align: "center",
      render: (text, record) => {
        return CurrencyFormatter(text ?? 0);
      },
    });

    (taxTypes ?? []).forEach((taxType) => {
      lineColumns.push({
        title: taxType?.name,
        dataIndex: "stockIssueLineTaxViews",
        align: "right",
        key: taxType?.code,
        render: (text, record) => {
          const taxEntry = record.stockIssueLineTaxViews?.find(
            (tax) => tax?.code === taxType?.code
          );
          return <b>{CurrencyFormatter(taxEntry?.totalTaxAmount ?? 0)}</b>;
        },
      });
    });

    return lineColumns;
  };

  const getStockItemColumns = () => {
    let columns = [];

    columns.push({
      title: "HSCode",
      dataIndex: ["stockType", "code"],
      key: "hscode",
    });

    columns.push({
      title: "Company Code",
      dataIndex: ["stockType", "secondaryCode"],
      key: "stockType",

      render: (text, record) => (
        <Tooltip placeholder="topLeft" title={text ?? ""}>
          {text?.substring(1, 8)}
        </Tooltip>
      ),
    });

    columns.push({
      title: "Description",
      dataIndex: ["stockType", "name"],
      key: "description",
    });

    columns.push({
      title: "Quantity",
      dataIndex: "forCastUnitForStockType",
      key: "forCastUnitForStockType",
      align: "left",
      render: (text, record) => {
        return text ?? "";
      },
    });
    columns.push({
      title: "VAT",
      dataIndex: ["totalVat"],
      key: "totalVat",
      align: "center",
      render: (text, record) => {
        return CurrencyFormatter(text ?? 0);
      },
    });

    columns.push({
      title: "Liability",
      dataIndex: ["totalDuty"],
      key: "totalDuty",
      align: "center",
      render: (text, record) => {
        return CurrencyFormatter(text ?? 0);
      },
    });

    // (taxTypes ?? []).forEach((taxType) => {
    //   columns.push({
    //     title: taxType?.name,
    //     dataIndex: "stockIssueLineTaxViews",
    //     align: "right",
    //     key: taxType?.code,
    //     render: (text, record) => {
    //       const taxEntry = record.stockIssueLineTaxViews?.find(
    //         (tax) => tax?.code === taxType?.code
    //       );
    //       return <b>{CurrencyFormatter(taxEntry?.totalTaxAmount ?? 0)}</b>;
    //     },
    //   });
    // });

    return columns;
  };

  const getColumnsTaxTable = () => {
    const columns = [
      {
        title: "Tax Code",
        dataIndex: "code",
        key: "Code",
        width: "10%",
        align: "center",
      },
      {
        title: "Tax Name",
        dataIndex: "name",
        key: "Name",
        align: "center",
        width: "15%",
      },
      {
        title: "Tax Amount",
        dataIndex: "totalTaxAmount",
        key: "Amount",
        width: "15%",
        align: "center",
        render: (text, record) => CurrencyFormatter(text ?? 0),
      },
    ];

    return columns;
  };

  const getSubItemColumns = () => {
    let columns = [];

    columns.push({
      title: "Cus.Dec.No",
      dataIndex: ["stockReceipt", "customRefNumber"],
      key: "customRefNumber",
      width: "10%",
    });

    columns.push({
      title: "Item",
      dataIndex: ["receiptLine", "itemNumber"],
      key: "itemNo",
      width: "5%",
    });

    columns.push({
      title: "Receipt Number",
      dataIndex: ["stockReceipt", "receiptNumber"],
      key: "recieiptNumber",
    });

    columns.push({
      title: "HSCode",
      dataIndex: "hscode",
      key: "hscode",
      render: (text, record) => {
        return record?.stockType?.code ?? "";
      },
    });

    columns.push({
      title: "Company Code",
      dataIndex: ["stockType", "secondaryCode"],
      key: "hscode",
      render: (text, record) => (
        <Tooltip title={text}>{text?.substring(0, 8)}</Tooltip>
      ),
    });

    columns.push({
      title: "Description",
      dataIndex: ["stockType", "name"],
      key: "stockType",
    });

    // columns.push({
    //   title: "Issued Line ID",
    //   dataIndex: ["stockIssueLineId"],
    //   key: "stockIssueLineId",
    // });

    columns.push({
      title: "Issued Packages",
      dataIndex: ["issuedPackages"],
      key: "issuedPackages",
      align: "right",
    });

    columns.push({
      title: "Quantity",
      dataIndex: ["tiepQuantityPerMaterial"],
      key: "tiepQuantityPerMaterial",
      align: "right",
    });
    if (stockIssue?.orderStage == "READY") {
      columns.push({
        title: "Action",
        key: "action",
        render: (text, record) => {
          if (record == null) return;
          return (
            <Button
              icon={<CloseOutlined />}
              type="link"
              onClick={() => handleItemDelete(record)}
            ></Button>
          );
        },
      });
    }

    return columns;
  };

  const handleOnChangeModalReceipt = (selected, option) => {
    const selectedStockReceipt =
      availableStocks.availableStockByReceiptViews.find(
        (receipt) => receipt.id.toString() === option.key
      );

    onFillModal(selectedStockReceipt);
  };

  const handleFormSubmitOnModal = async () => {
    try {
      const validatedValues = await form.validateFields([
        "selectedStockReceipt",
      ]);
      const values = validatedValues["selectedStockReceipt"];
      if (values.quantity <= 0) return;
      setLoading(true);

      const selectedLineId = selectedLine?.id;

      const params = {
        stockReceipt: {
          id: values.id,
        },
        stockType: {
          id: values.stockTypeId,
        },
        quantity: values.quantity,
      };

      await stockIssueService.addItem(selectedLineId, params);
      await getStockIssue(id);
      message.success("Item added successfully!");
    } catch (error) {
      setError(error);
      message.error(error);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <div>
      <Spin tip="Loading..." spinning={loading}>
        <PageHeader
          title={
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/dashboard">Dashboard</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/stock-issue">Export List</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to={`${path}`.replace(":id", id)}>
                  {stockIssue.issueNumber} ::{" "}
                  <Tag
                    color={`${
                      stockIssue.orderStage === "ISSUED"
                        ? "#2db7f5"
                        : stockIssue.orderStage === "IN_PROGRESS"
                        ? "#108ee9"
                        : "#87d068"
                    }`}
                  >
                    {stockIssue.orderStage == "READY"
                      ? "UPDATE"
                      : stockIssue.orderStage}
                  </Tag>
                </Link>
              </Breadcrumb.Item>
            </Breadcrumb>
          }
          extra={getActionButtons()}
        >
          <Page inner error={error}>
            <Stepper status={stockIssue?.orderStage ?? "OPEN"} />
            <TabMenu menu="summary" id={id} />
            <Form
              layout="vertical"
              form={form}
              name="form-create"
              requiredMark={false}
            >
              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item
                    name={["storeName"]}
                    label="Store"
                    rules={[{ required: true }]}
                  >
                    {/* <TextArea
                      rows={5}
                      readOnly
                      style={{
                        height: 120,
                        resize: "none",
                      }}
                    /> */}
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name={["storeName"]}
                    label="Consignee Address"
                    rules={[{ required: true }]}
                  >
                    {/* <TextArea
                      rows={5}
                      readOnly
                      style={{
                        height: 120,
                        resize: "none",
                      }}
                    /> */}
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="issuedDate"
                    label="Issue Date"
                    rules={[{ required: true }]}
                  >
                    <Input style={{ minWidth: "150px" }} readOnly />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    name="customReferenceNumber"
                    label="Cus.Dec.No#"
                    rules={[{ required: true }]}
                  >
                    <Input style={{ minWidth: "150px" }} readOnly />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item name="stockIssueType" label="Export Type">
                    <Input
                      readOnly
                      disabled
                      style={{
                        fontSize: "1.1rem",
                        width: "100%",
                        border: "0px",
                        fontWeight: "bold",
                      }}
                      variant="borderless"
                    />
                  </Form.Item>
                </Col>
              </Row>
              {stockIssue && (
                <Row
                  gutter={[24, 24]}
                  style={{
                    marginBottom: "10px",
                  }}
                >
                  <Col span={8}>
                    <Table
                      size="small"
                      bordered={false}
                      pagination={false}
                      dataSource={stockIssue?.stockIssueTaxViews || []}
                      columns={getColumnsTaxTable()}
                      rowKey={(record) => record.code || Math.random()}
                    />
                  </Col>

                  <Col span={8}></Col>
                  <Col span={8}></Col>
                </Row>
              )}
              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item name="totalVatAmount" label="Total VAT">
                    <Statistic precision={2} prefix="LKR" />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    name="totalLiabilityAmount"
                    label="Total Liability"
                  >
                    <Statistic precision={2} prefix="LKR" />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item name="totalAmount" label="Total Amount">
                    <Statistic precision={2} prefix="LKR" />
                  </Form.Item>
                </Col>
                {/* <Col span={6}>
                  <Form.Item
                    name="totalSslAmount"
                    label="SSL"
                    rules={[{ required: true }]}
                  >
                    <Input style={{ minWidth: "150px" }} readOnly />
                  </Form.Item>
                </Col> */}
              </Row>

              {stockIssue && (
                <Table
                  bordered
                  rowKey="id"
                  pagination={false}
                  columns={getLineColumns()}
                  dataSource={stockIssue?.stockIssueLines}
                  rowSelection={lineRowSelection}
                  size="medium"
                />
              )}

              <Divider orientation="left">Raw materials Summary</Divider>
              {selectedLine && (
                <Table
                  bordered
                  size="small"
                  rowKey={(record) => record.stockType.id}
                  columns={getStockItemColumns()}
                  rowSelection={issueItemsRowSelection}
                  dataSource={selectedLine?.groupedStockIssueLineStockItemViews}
                  pagination={false}
                />
              )}

              <Divider orientation="left">Material Details</Divider>

              {/* {availableStocks && ( */}
              <Modal
                title={`Edit Material - ${form.getFieldValue([
                  "selectedStockReceipt",
                  "stockTypeName",
                ])}`}
                width={1000}
                open={open}
                onOk={handleFormSubmitOnModal}
                onCancel={() => setOpen(false)}
                getContainer={false} // Renders Modal content in-place without portaling which breaks the form layout
              >
                <Row gutter={24}>
                  <Col span={8}>
                    <Form.Item
                      name={["selectedStockReceipt", "customRefNumber"]}
                      label="Cusdec No#"
                    >
                      <Select
                        style={{ width: "100%" }}
                        placeholder="Select Stock"
                        onChange={handleOnChangeModalReceipt}
                      >
                        {availableStocks?.availableStockByReceiptViews?.map(
                          (item) => (
                            <Select.Option
                              key={item.id}
                              value={item.customRefNumber}
                            >
                              {item.customRefNumber}
                            </Select.Option>
                          )
                        )}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name={["selectedStockReceipt", "stockTypeName"]}
                      label="Stock Type"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name={["selectedStockReceipt", "stockTypeCode"]}
                      label="HS Code"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col span={8}>
                    <Form.Item
                      name={["selectedStockReceipt", "packingType"]}
                      label="Packing Type"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name={["selectedStockReceipt", "totalQuantity"]}
                      label="Package Quantity"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name={["selectedStockReceipt", "receivedDate"]}
                      label="Received Date"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col span={8}>
                    <Form.Item
                      name={["selectedStockReceipt", "uomName"]}
                      label="UOM"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name={["selectedStockReceipt", "tiepQuantityPerMaterial"]}
                      label="UOM Quantity"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name={["selectedStockReceipt", "quantity"]}
                      label="New Quantity"
                      rules={[
                        { required: true, message: "Quantity is required" },
                      ]}
                    >
                      <Input type="number" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item name={["selectedStockReceipt", "id"]} hidden>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={["selectedStockReceipt", "stockTypeId"]}
                      hidden
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </Modal>
              {/* )} */}

              {selectedLine?.groupedStockIssueLineStockItemViews && (
                <Tabs
                  onChange={handleTabChange}
                  activeKey={activeTabKey}
                  type="card"
                >
                  {getTabContentOfSubItems(
                    selectedLine?.groupedStockIssueLineStockItemViews
                  )}
                </Tabs>
              )}
            </Form>
          </Page>
        </PageHeader>
      </Spin>
    </div>
  );
}

export default TiepSummary;
